import React, { Suspense, PropsWithChildren, useEffect } from 'react';
import { Route, Routes, Navigate, useLocation, Outlet } from 'react-router-dom';
import { useGlobalDispatch, useGlobalState } from '../common/context/AppContext';
import { saveToObjectCache } from '../common/actions/ObjectCacheActions';
import { IAppProps, SCREEN_NAME } from '../common/interface/IAppProvider';
import { track } from 'c9-tracking';
import { stripTrailingSlashes } from '@flos/react-components';
import usePreviousRoute from '../common/customHooks/usePreviousRoute';
import AddressScreenView from '../views/address/AddressScreen.view';

const CustomerUser = React.lazy(
    () => import(/*webpackChunkName: 'CustomerUserScreen' */ '../views/customerUser/customerUserScreen.view')
);
const HouseholdScreenView = React.lazy(
    () => import(/*webpackChunkName: 'HouseholdScreen' */ '../views/household/HouseholdScreen.view')
);
const AlreadyCoveredScreenView = React.lazy(
    () => import(/*webpackChunkName: 'AlreadyCoveredScreen' */ '../views/already-covered/AlreadyCoveredScreen.view')
);
const AgeScreenView = React.lazy(() => import(/*webpackChunkName: 'AgeScreenView' */ './../views/age/AgeScreen.view'));
const PreconditionsScreenView = React.lazy(
    () => import(/*webpackChunkName: 'PreconditionsScreenView' */ './../views/preconditions/PreconditionsScreen.view')
);
const PackageScreenView = React.lazy(
    () => import(/*webpackChunkName: 'PackageScreenView' */ './../views/packages/PackageScreen.view')
);
const ShoppingBasketScreen = React.lazy(
    () => import(/*webpackChunkName: 'ShoppingBasketScreen' */ './../views/shopping-basket/ShoppingBasketScreen.view')
);
const BornholmScreenView = React.lazy(
    () => import(/*webpackChunkName: 'BornholmScreenView' */ './../views/bornholm/BornholmScreen.view')
);
const AboutYouScreenView = React.lazy(
    () => import(/*webpackChunkName: 'AboutYouScreenView' */ '../views/about-you/AboutYouScreen.view')
);
const ExitScreenView = React.lazy(
    () => import(/*webpackChunkName: 'ExitScreenView' */ '../views/exit/ExitScreen.view')
);
const AgeErrorScreenView = React.lazy(
    () => import(/*webpackChunkName: 'ErrorScreenView' */ '../views/error/AgeErrorScreen.view')
);
const ClaimScreenView = React.lazy(
    () => import(/*webpackChunkName: 'ClaimScreenView' */ '../views/claim/ClaimScreen.view')
);
const ClaimTypeScreenView = React.lazy(
    () => import(/*webpackChunkName: 'ClaimTypeScreen' */ '../views/claim-type/ClaimTypeScreen.view')
);
const TerminationScreenView = React.lazy(
    () => import(/*webpackChunkName: 'TerminationScreenView' */ '../views/termination/TerminationScreen.view')
);
const PaymentScreenView = React.lazy(
    () => import(/*webpackChunkName: 'PaymentScreenView' */ '../views/payment/PaymentScreenView')
);
const PaymentOptionsScreenView = React.lazy(
    () => import(/*webpackChunkName: 'PaymentOptionsScreenView' */ '../views/payment-options/PaymentOptionsScreen.view')
);
const ReceiptScreenView = React.lazy(
    () => import(/*webpackChunkName: 'ReceiptScreenView' */ '../views/receipt/ReceiptScreen.view')
);
const ComplianceScreenView = React.lazy(
    () => import(/*webpackChunkName: 'ComplianceScreenView' */ '../views/compliance/ComplianceScreen.view')
);
const CallMeScreenView = React.lazy(
    () => import(/*webpackChunkName: 'CallMeScreenView' */ '../views/call-me/CallMeScreen.view')
);
const PageNotFoundScreenView = React.lazy(
    () => import(/*webpackChunkName: 'PageNotFoundScreenView' */ '../views/pageNotFound/PageNotFoundScreen.view')
);

/* ============================================ Exported Constants ============================================ */
export enum ROUTE_NAMES {
    INTRO = '/', // 0
    ALREADY_CUSTOMER = '/kunde-eller-ej', // 1
    HOUSEHOLD = '/husstand', // 2
    ALREADY_COVERED = '/eksisterende-indbo', // 3
    CLAIM = '/skader', // 4
    CLAIMTYPE = '/skadetype', // 5
    AGE = '/alder', // 6
    PRECONDITIONS = '/forudsaetninger', // 7
    PACKAGES = '/pakker', // 8
    DEDUCTIBLES = '/selvrisiko', // 9
    BASKET = '/indkoebskurv', // 10
    ABOUT_YOU = '/om-dig', // 11
    TERMINATION = '/opsigelse', // 12
    PAYMENT_1 = '/betalingsmaade', // 13
    PAYMENT_2 = '/betalingsfrekvens', // 14
    COMPLIANCE = '/betingelser', // 15
    RECEIPT = '/', // 16
    BORNHOLM = '/bornholm',
    CALL_ME = '/ring-mig-op',
    EXIT = '/afslut',
    PAGE_NOT_FOUND = '/404',
    AGE_ERROR = '/fejl'
}

/* ============================================ Local Components ============================================ */
function RouterCondition(): JSX.Element {
    const globalState = useGlobalState();
    const location = useLocation();

    // If there is an error
    if (globalState.hasError) {
        return <Navigate to={ROUTE_NAMES.CALL_ME} />;
    }

    // User didn't start the flow and trying to jump to the middle
    if (globalState.initialState && location.pathname !== ROUTE_NAMES.INTRO) {
        return <Navigate to={ROUTE_NAMES.INTRO} />;
    }

    return <Outlet />;
}

/* ============================================ Main Component ============================================ */
const RouterApp = (props: PropsWithChildren<IAppProps>): JSX.Element => {
    const globalState = useGlobalState();
    const appDispatch = useGlobalDispatch();
    const location = useLocation();
    const prevLocation = usePreviousRoute(location);
    const isReceipt = props.config.baseName.indexOf('kvittering') !== -1;
    const historyPath = location.pathname;
    const pathname = historyPath.slice(-1) === '/' && historyPath.length > 1 ? historyPath.slice(0, -1) : historyPath;
    const index = Object.values(ROUTE_NAMES).findIndex((item) => {
        const regex = new RegExp(item + '/?$');
        return regex.test(pathname);
    });

    useEffect(() => {
        if (!globalState.initialState && globalState.stateChange) {
            saveToObjectCache(props, globalState, appDispatch);
            appDispatch({ type: SCREEN_NAME.SET_STATE_CHANGE, payload: false });
        }
    }, [props, appDispatch, globalState]);

    useEffect(() => {
        if (index !== globalState.actualStep && index !== -1) {
            appDispatch({ type: SCREEN_NAME.SET_PROGRESS, payload: index });
        }
    }, [appDispatch, index, globalState.actualStep]);

    useEffect(() => {
        if (
            prevLocation !== location &&
            !(location.pathname === '/kvittering' || location.pathname === ROUTE_NAMES.INTRO) &&
            !isReceipt
        ) {
            track.navigate(document.URL, `Indboforsikring | ${stripTrailingSlashes(location.pathname)}`);
        }
    }, [prevLocation, location, isReceipt]);

    return (
        <Suspense fallback={null}>
            <Routes>
                <Route element={<RouterCondition />}>
                    <Route
                        path={isReceipt ? `${ROUTE_NAMES.RECEIPT}` : `${ROUTE_NAMES.INTRO}`}
                        element={isReceipt ? <ReceiptScreenView {...props} /> : <AddressScreenView {...props} />}
                    />
                    <Route path={`${ROUTE_NAMES.BORNHOLM}`} element={<BornholmScreenView />} />
                    <Route path={`${ROUTE_NAMES.ALREADY_CUSTOMER}`} element={<CustomerUser {...props} />} />
                    <Route path={`${ROUTE_NAMES.HOUSEHOLD}`} element={<HouseholdScreenView {...props} />} />
                    <Route path={`${ROUTE_NAMES.ALREADY_COVERED}`} element={<AlreadyCoveredScreenView {...props} />} />
                    <Route path={`${ROUTE_NAMES.CLAIM}`} element={<ClaimScreenView />} />
                    <Route path={`${ROUTE_NAMES.CLAIMTYPE}`} element={<ClaimTypeScreenView />} />
                    <Route path={`${ROUTE_NAMES.COMPLIANCE}`} element={<ComplianceScreenView {...props} />} />
                    <Route path={`${ROUTE_NAMES.AGE}`} element={<AgeScreenView {...props} />} />
                    <Route path={`${ROUTE_NAMES.PRECONDITIONS}`} element={<PreconditionsScreenView {...props} />} />
                    <Route path={`${ROUTE_NAMES.PACKAGES}`} element={<PackageScreenView {...props} />} />
                    <Route path={`${ROUTE_NAMES.BASKET}`} element={<ShoppingBasketScreen {...props} />} />
                    <Route path={`${ROUTE_NAMES.ABOUT_YOU}`} element={<AboutYouScreenView {...props} />} />
                    <Route path={`${ROUTE_NAMES.TERMINATION}`} element={<TerminationScreenView {...props} />} />
                    <Route path={`${ROUTE_NAMES.PAYMENT_1}`} element={<PaymentScreenView {...props} />} />
                    <Route path={`${ROUTE_NAMES.PAYMENT_2}`} element={<PaymentOptionsScreenView {...props} />} />
                </Route>

                <Route path={`${ROUTE_NAMES.EXIT}`} element={<ExitScreenView />} />
                <Route path={`${ROUTE_NAMES.CALL_ME}`} element={<CallMeScreenView {...props} />} />
                <Route path={`${ROUTE_NAMES.AGE_ERROR}`} element={<AgeErrorScreenView />} />
                <Route path={`${ROUTE_NAMES.PAGE_NOT_FOUND}`} element={<PageNotFoundScreenView />} />
                <Route path="*" element={<Navigate to={`${ROUTE_NAMES.PAGE_NOT_FOUND}`} />} />
            </Routes>
        </Suspense>
    );
};

export default RouterApp;
